import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import "../styles/components/CategoryShowcase.scss";

const TAGLINES = {
  Products: "Ready-made luxury pieces",
  "3D Printers": "Precision machines for creators",
  Filaments: "Premium materials, engineered for detail",
  Accessories: "Refined finishing touches",
};

const AUTO_ADVANCE_MS = 5000;

function CategoryShowcase({ categories }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Slides are stacked absolutely and only opacity-toggled (see .scss), so
  // they're all within the viewport the whole time - native loading="lazy"
  // can't defer them (the browser sees them as visible immediately). Only
  // mount an <img> once a slide has actually been shown at least once, so
  // the 3 non-visible root-category hero images don't compete for
  // bandwidth with the one the visitor is actually looking at.
  const [visitedIndices, setVisitedIndices] = useState(() => new Set([0]));

  useEffect(() => {
    setVisitedIndices((prev) =>
      prev.has(activeIndex) ? prev : new Set(prev).add(activeIndex)
    );
  }, [activeIndex]);

  const supportsHover = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches,
    []
  );

  const prefersReducedMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const goTo = useCallback(
    (index) => {
      const count = categories.length;
      setActiveIndex(((index % count) + count) % count);
    },
    [categories.length]
  );

  useEffect(() => {
    if (paused || prefersReducedMotion || categories.length <= 1) {
      return undefined;
    }

    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % categories.length);
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(timerRef.current);
  }, [paused, prefersReducedMotion, categories.length]);

  const imageUrl = (category) =>
    category.image
      ? `${API_BASE_URL}/images/categories/${category.image}`
      : null;

  const handlePointerMove = (e) => {
    if (!supportsHover) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width;
    const relY = (e.clientY - rect.top) / rect.height;

    e.currentTarget.style.setProperty("--parallax-x", `${((0.5 - relX) * 14).toFixed(1)}px`);
    e.currentTarget.style.setProperty("--parallax-y", `${((0.5 - relY) * 14).toFixed(1)}px`);
  };

  return (
    <div
      className="showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="showcase-slides">
        {categories.map((category, index) => {
          const isActive = index === activeIndex;
          const url = imageUrl(category);
          const isLoaded = visitedIndices.has(index);

          const linked = category.link_to_category !== false;
          const SlideTag = linked ? Link : "div";

          return (
            <SlideTag
              key={category.id}
              {...(linked ? { to: `/c/${category.id}` } : {})}
              className={`slide ${isActive ? "is-active" : ""} ${linked ? "" : "slide--static"}`}
              aria-hidden={!isActive}
              tabIndex={linked ? (isActive ? 0 : -1) : undefined}
              onMouseMove={handlePointerMove}
            >
              <div className="slide-media">
                {url && isLoaded ? (
                  <img
                    src={url}
                    alt={category.name}
                    fetchpriority={index === 0 ? "high" : "auto"}
                  />
                ) : (
                  <div className="slide-placeholder" aria-hidden="true" />
                )}
                <div className="slide-scrim" />
              </div>

              <div className="slide-content">
                <h2>{category.name}</h2>
                {TAGLINES[category.name] && <p>{TAGLINES[category.name]}</p>}
                {linked && (
                  <span className="slide-explore">
                    Explore <span className="arrow" aria-hidden="true">&rarr;</span>
                  </span>
                )}
              </div>
            </SlideTag>
          );
        })}
      </div>

      <div className="showcase-controls">
        <button
          type="button"
          className="showcase-arrow prev"
          aria-label="Previous category"
          onClick={() => goTo(activeIndex - 1)}
        >
          &#8592;
        </button>

        <div className="showcase-dots">
          {categories.map((category, index) => (
            <button
              key={category.id}
              type="button"
              className={`dot ${activeIndex === index ? "active" : ""}`}
              aria-label={`Go to ${category.name}`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>

        <button
          type="button"
          className="showcase-arrow next"
          aria-label="Next category"
          onClick={() => goTo(activeIndex + 1)}
        >
          &#8594;
        </button>
      </div>
    </div>
  );
}

export default CategoryShowcase;
